import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useOrganisation } from "@/context/OrganisationContext";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";

interface AuthRedirectorProps {
  children: React.ReactNode;
}

export const AuthRedirector = ({ children }: AuthRedirectorProps) => {
  const { isAuthenticated, isLoading, isOnboarding } = useAuth();
  const { hasOrganisation } = useOrganisation();
  const location = useLocation();

  // Check for onboarding completion parameters
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      const params = new URLSearchParams(location.search);
      const onboardingComplete = params.get("onboarding") === "complete";
      const transferData = params.get("transfer") === "true";

      if (onboardingComplete && transferData) {
        console.log("AuthRedirector: Detected onboarding completion parameters");
        supabase.auth.getUser().then(({ data }) => {
          if (data?.user?.id) {
            console.log("AuthRedirector: Transferring data for user:", data.user.id);
            import("@/api/onboardingImport").then(({ transferDataToDatabase }) => {
              transferDataToDatabase(data.user.id).catch((error) => {
                console.error("Error transferring data:", error);
              });
            });
          }
        });
      }
    }
  }, [isAuthenticated, isLoading, location.search]);

  console.log("AuthRedirector: Rendering with state:", {
    isAuthenticated,
    isLoading,
    isOnboarding,
    hasOrganisation,
    path: location.pathname,
  });

  if (isLoading) {
    console.log("AuthRedirector: Showing loading state");
    return (
      <div className="flex items-center justify-center h-screen w-screen">
        <LoadingSpinner message="Checking organization..." />
      </div>
    );
  }

  if (isAuthenticated && !isOnboarding && hasOrganisation && location.pathname === "/") {
    console.log(
      "AuthRedirector: Authenticated user with organization on landing page, redirecting to dashboard"
    );
    return <Navigate to="/dashboard" replace />;
  }

  console.log(
    "AuthRedirector: Showing content for",
    isAuthenticated ? "authenticated" : "unauthenticated",
    "user"
  );
  return <>{children}</>;
};
